import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import AssistantFAB from './AssistantFAB';
import GlobalAssistant from './GlobalAssistant';
import { ActivityProvider } from './ActivityProvider';
import MobileNavigation from './MobileNavigation';
import GenerationStatusPlate from '../generation-status/GenerationStatusPlate.jsx';
import PendingOAuthBanner from '../PendingOAuthBanner.jsx';
import { NotInFreePreview } from '../../contexts/FreePreviewContext.jsx';
// NOTE: BatchActivityBridge intentionally removed. The new
// GenerationStatusContentFeedBridge (in App.jsx) is the single source of
// truth for batch generation progress. Keeping the old bridge alive
// caused a duplicate "Generating 0 of 1" pill in the topbar that no
// longer matched the new floating plate (esp. for image_post batches
// where the old pill closed at text-done but the plate kept tracking
// the image generation). ActivityProvider stays mounted so per-screen
// short-lived activities (EditPost "Saving…", etc.) still surface.

export default function AppShell({ topbarTitle, topbarSub, topbarRight, initialActivity = null, hasStickyFooter = false, children }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname, search } = useLocation();
  const navigationRef = useRef(null);
  const menuButtonRef = useRef(null);
  const restoreMenuFocus = useRef(true);
  const closeMobileNav = (options = {}) => {
    restoreMenuFocus.current = options.restoreFocus !== false;
    setMobileNavOpen(false);
  };

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    const desktop = window.matchMedia?.('(min-width: 769px)');
    const close = () => { if (desktop?.matches) setMobileNavOpen(false); };
    desktop?.addEventListener('change', close);
    return () => desktop?.removeEventListener('change', close);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const panel = navigationRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel?.querySelector('.sb-close')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
      if (e.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll('a[href],button:not([disabled]),select:not([disabled]),[tabindex="0"]')]
        .filter(item => item.getClientRects().length > 0);
      const first = items[0];
      const last = items.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      if (restoreMenuFocus.current && (panel?.contains(document.activeElement) || document.activeElement === document.body)) {
        menuButtonRef.current?.focus();
      }
    };
  }, [mobileNavOpen]);

  return (
    <ActivityProvider initial={initialActivity}>
      <div className={`app-shell has-mobile-navigation ${mobileNavOpen ? 'is-mobile-nav-open' : ''} ${hasStickyFooter ? 'has-sticky-footer' : ''}`}>
        {mobileNavOpen && (
          <div
            className="app-shell-backdrop"
            onClick={() => setMobileNavOpen(false)}
            role="presentation"
          />
        )}
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={closeMobileNav} panelRef={navigationRef} mobileMenuButtonRef={menuButtonRef} />
        <div className="app-main" inert={mobileNavOpen ? '' : undefined}>
          <Topbar
            title={topbarTitle}
            sub={topbarSub}
            right={topbarRight}
          />
          <PendingOAuthBanner appRoute />
          <GenerationStatusPlate />
          <main className="app-content">{children}</main>
        </div>
        <MobileNavigation open={mobileNavOpen} onOpen={() => { restoreMenuFocus.current = true; setMobileNavOpen(true); }} menuButtonRef={menuButtonRef} />
        {/* GH-1133 S5 (§2.2): the assistant's writes 402 before the card,
            so the free preview does not offer it. */}
        <NotInFreePreview>
          <AssistantFAB />
          <GlobalAssistant />
        </NotInFreePreview>
      </div>
    </ActivityProvider>
  );
}
